import { ArrowUpDown, ChevronDown } from 'lucide-react';
import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput, type SearchInputProps } from '../Search';
import { Spinner } from '../Spinner';
import { Badge } from '../../ui/Badge/Badge';
import { Button } from '../../ui/Button/Button';
import { Input } from '../../ui/Input/Input';
import { Checkbox } from '../../ui/Checkbox/Checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/Table/Table';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/Popover/Popover';
import { InfiniteScrollSentinel } from './InfiniteScrollSentinel';
import { TableEmptyState, type TableEmptyStateProps } from './TableEmptyState';
// Define the Table context type
type TableContextType = {
  data: any[];
  isLoading: boolean;
  /** Props for SearchInput; always set (from searchProps or derived from internal state). */
  searchInputProps: SearchInputProps;
  sortColumn?: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  searchFields?: string[];
  statusBadgeConfig?: StatusBadgeConfigType;
  // Infinite scroll props
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  // Row selection props
  enableRowSelection?: boolean;
  selectedRowIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  getRowId?: (row: any, index: number) => string;
};

// New type for status badge configuration
export interface StatusBadgeConfigType {
  getStatusClass: (status: string) => string;
  getStatusDisplayText: (status: string) => string;
  showIcon?: boolean;
  getStatusIcon?: (status: string) => React.ReactNode;
}

export interface TableProps {
  data: any[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchFields?: string[];
  /** When provided, search input is controlled by this. Otherwise uses internal state only. */
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  statusBadgeConfig?: StatusBadgeConfigType;
  // Infinite scroll props (required)
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadingLabel?: string;
  endLabel?: string;
  children: React.ReactNode;
  // Row selection props
  enableRowSelection?: boolean;
  selectedRowIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  getRowId?: (row: any, index: number) => string;
  /**
   * Custom empty-state node shown ONLY in the **initial** empty state — i.e. when the table
   * has no data AND no filters/search are active. In this mode the table header, columns,
   * toolbar (`children`), and infinite-scroll sentinel are NOT rendered; only the empty
   * state is shown so the user gets a clean "nothing here yet" surface.
   *
   * Takes precedence over `emptyStateProps`.
   */
  emptyState?: React.ReactNode;
  /**
   * Convenience: render the shared `TableEmptyState` with these props in the initial empty
   * state. Ignored when `emptyState` is also provided.
   */
  emptyStateProps?: TableEmptyStateProps;
  /**
   * Whether the user has any active search/filter/sort. When `true`, the empty state is
   * suppressed and the normal table chrome is rendered with a small inline
   * "no results found" message — so the user can clear filters and try again.
   * Defaults to `false`.
   */
  hasActiveFilters?: boolean;
}

export interface BadgeOption {
  /** Unique value for the badge */
  value: string;
  /** Display label (can be a translation key) */
  label: string;
  /** Optional count to display (used as fallback if counts prop is not provided) */
  count?: number;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Optional custom class for styling */
  className?: string;
}

/**
 * Badge count entry for dynamic counts
 * Used when counts need to be updated separately from options
 */
export interface BadgeCount {
  /** Value that matches an option's value */
  value: string;
  /** Count to display for this option */
  count: number;
}

interface BadgeProps {
  /** Manual children (for backward compatibility) */
  children?: React.ReactNode;
  /**
   * Array of badge options (static - defines which badges are visible)
   * This array should remain stable to prevent badge flickering
   */
  options?: BadgeOption[];
  /**
   * Array of counts to display next to badges (dynamic - updates frequently)
   * If provided, counts from this array take priority over option.count
   * Count resolution: counts prop > option.count > 0
   */
  counts?: BadgeCount[];
  /** Currently selected value */
  value?: string;
  /** Callback when a badge is clicked */
  onChange?: (value: string | undefined) => void;
  /** Show counts on badges */
  showCounts?: boolean;
  /**
   * Display mode:
   * - 'inline': Render all options as separate badges (default)
   * - 'dropdown': Single badge that opens a popover with options
   */
  mode?: 'inline' | 'dropdown';
  /**
   * Placeholder text when no option is selected (dropdown mode only)
   */
  placeholder?: string;
}

interface BadgeItemProps {
  count?: number;
  label: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
  isActive?: boolean;
  onClick?: () => void;
  /** Whether to show the count badge (defaults to false) */
  showCounter?: boolean;
  /** Optional icon to display before the label */
  icon?: React.ReactNode;
}

interface RowsProps {
  columns: Array<{
    key: string;
    label: string;
    sortable?: boolean;
    className?: string;
  }>;
  rowRender: (item: any, index: number) => React.ReactNode;
  children?: React.ReactNode;
  enableRowSelection?: boolean;
  /** Applied to the underlying `<table>` element (e.g. `table-fixed w-full`). */
  tableClassName?: string;
  /** Optional `<colgroup>` for fixed column widths (used with `table-fixed`). */
  colGroup?: React.ReactNode;
  /**
   * @deprecated Prefer `colGroup` + `table-fixed w-full` for full-width tables.
   */
  trailingSpacer?: boolean;
}

// Create the context - export it so it can be used by other components
export const TableContext = createContext<TableContextType | undefined>(undefined);

// Hook to use the context
const useTableContext = () => {
  const context = useContext(TableContext);
  if (!context) {
    throw new Error('Table compound components must be used within a Table component');
  }
  return context;
};

/**
 * Main Table component - A compound component for flexible, customizable tables
 * Now supports infinite scroll only (no page-based pagination)
 */
export function SharedTable({
  data = [],
  isLoading = false,
  sortColumn,
  sortDirection = 'asc',
  searchFields = [],
  searchProps,
  onSort,
  statusBadgeConfig,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  loadingLabel = 'Loading…',
  endLabel = 'No more items',
  children,
  enableRowSelection = false,
  selectedRowIds = new Set(),
  onSelectionChange,
  getRowId,
  emptyState,
  emptyStateProps,
  hasActiveFilters = false,
}: TableProps) {
  const { t } = useTranslation(['c-main']);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const handleInternalSearch = useCallback((term: string) => {
    setInternalSearchTerm(term);
  }, []);

  // Always provide searchInputProps: from searchProps or derived from internal state
  const searchInputProps: SearchInputProps = useMemo(
    () =>
      searchProps ?? {
        term: internalSearchTerm,
        setTerm: handleInternalSearch,
        placeholder: t('search.placeholder'),
      },
    [searchProps, internalSearchTerm, handleInternalSearch, t],
  );

  // Get row ID helper
  const getRowIdValue = useCallback(
    (row: any, index: number): string => {
      if (getRowId) {
        return getRowId(row, index);
      }
      // Default: try _id field, otherwise use index
      return row?._id?.toString() || String(index);
    },
    [getRowId],
  );

  // Context value to share with all subcomponents
  const contextValue = {
    data,
    isLoading,
    searchInputProps,
    sortColumn,
    sortDirection,
    onSort:
      onSort ||
      ((column: string) => {
        /* Default empty implementation */
      }),
    searchFields,
    statusBadgeConfig,
    // Infinite scroll props
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    // Row selection props
    enableRowSelection,
    selectedRowIds,
    onSelectionChange,
    getRowId: getRowIdValue,
  };

  // Initial empty state: no data, not loading, no active filters, and an empty-state node
  // is provided. In this mode we render ONLY the empty state — no header chrome, no columns,
  // no infinite-scroll sentinel — so the user sees a clean "nothing here yet" surface.
  const hasEmptyStateContent = emptyState !== undefined || emptyStateProps !== undefined;
  const showInitialEmptyState =
    !isLoading && data.length === 0 && !hasActiveFilters && hasEmptyStateContent;

  return (
    <TableContext.Provider value={contextValue}>
      <div className="w-full p-6 bg-white border rounded-lg">
        {showInitialEmptyState ? (
          emptyState ?? <TableEmptyState {...(emptyStateProps as TableEmptyStateProps)} />
        ) : (
          <>
            {children}
            {/* Infinite scroll sentinel - always shown at bottom when table chrome is visible */}
            <InfiniteScrollSentinel
              onVisible={onLoadMore}
              active={hasNextPage && !isFetchingNextPage}
              loading={isFetchingNextPage}
              labelLoading={loadingLabel}
              labelEnd={endLabel}
              showEnd={!hasNextPage && data.length > 0}
            />
          </>
        )}
      </div>
    </TableContext.Provider>
  );
}

/**
 * Head container for search and filter elements
 */
function Head({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-between">{children}</div>;
}

/**
 * Mid container for the main table content
 */
function Mid({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col w-full mt-4">
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-black ring-opacity-5">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Search component for filtering the table.
 * Uses shared SearchInput; debounce is inside SearchInput.
 */
function SearchComponent() {
  const { searchInputProps } = useTableContext();
  return <SearchInput {...searchInputProps} />;
}

/**
 * Badge component for displaying filter options with counts.
 *
 * Count resolution priority:
 * 1. If `counts` prop is provided, use count from counts array (for dynamic counts from API)
 * 2. Otherwise, fall back to `option.count` (for static counts in options)
 * 3. If neither exists, default to 0
 *
 * This allows two usage patterns:
 * - Dynamic counts: Pass static `options` + dynamic `counts` array (prevents badge flicker)
 * - Static counts: Pass `options` with embedded `count` property (simpler for static data)
 *
 * Can work in three modes:
 * 1. Manual mode: Pass children (for backward compatibility)
 * 2. Inline mode: Pass options array, renders all as separate badges
 * 3. Dropdown mode: Pass options with mode="dropdown", shows single badge with popover
 *
 * @example
 * ```tsx
 * // Dynamic counts (recommended for data that changes)
 * <SharedTable.Badge
 *   options={[{ value: 'all', label: 'All' }, { value: 'new', label: 'New' }]}
 *   counts={[{ value: 'all', count: 10 }, { value: 'new', count: 5 }]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 *
 * // Static counts (for data that doesn't change)
 * <SharedTable.Badge
 *   options={[
 *     { value: 'all', label: 'All', count: 10 },
 *     { value: 'new', label: 'New', count: 5 }
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 * ```
 */
function StatusBadge({
  children,
  options,
  counts,
  value,
  onChange,
  showCounts = true,
  mode = 'inline',
  placeholder = 'Select',
}: BadgeProps) {
  const [open, setOpen] = useState(false);

  // Create a map of counts for quick lookup (if counts prop is provided)
  const countsMap = useMemo(() => {
    if (!counts) return new Map<string, number>();
    return new Map(counts.map(c => [c.value, c.count]));
  }, [counts]);

  // Merge options with counts using fallback logic
  const optionsWithCounts = useMemo(() => {
    if (!options) return [];
    return options.map(option => {
      // Priority: counts prop > option.count > 0
      let finalCount: number;
      if (counts && countsMap.has(option.value)) {
        // Use count from counts array (dynamic)
        finalCount = countsMap.get(option.value)!;
      } else if (option.count !== undefined) {
        // Fall back to option.count (static)
        finalCount = option.count;
      } else {
        // Default to 0
        finalCount = 0;
      }

      return {
        ...option,
        count: finalCount,
      };
    });
  }, [options, countsMap, counts]);

  // Dropdown mode: single badge with popover
  if (optionsWithCounts && optionsWithCounts.length > 0 && mode === 'dropdown') {
    const selectedOption = optionsWithCounts.find(opt => opt.value === value);
    const displayLabel = selectedOption ? selectedOption.label : placeholder;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className={`
              group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg 
              transition-all duration-200 cursor-pointer select-none
              ${
                selectedOption
                  ? 'ring-2 ring-primary ring-offset-2 bg-primary text-primary-foreground font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}>
            <div className="flex items-center gap-2 whitespace-nowrap">
              {selectedOption?.icon && (
                <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                  {selectedOption.icon}
                </div>
              )}
              <span className="text-sm font-medium">{displayLabel}</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start" sideOffset={5}>
          <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
            {/* Clear option */}
            {value && (
              <div
                onClick={e => {
                  e.stopPropagation();
                  if (onChange) {
                    onChange(undefined);
                  }
                  setTimeout(() => setOpen(false), 150);
                }}
                className="flex items-center justify-between px-3 py-2 rounded-md transition-all duration-150 cursor-pointer select-none hover:bg-gray-100 text-gray-700 border-b border-gray-200 mb-1">
                <span className="text-sm font-medium">Clear</span>
                <span className="text-xs">×</span>
              </div>
            )}
            {optionsWithCounts.map(option => {
              const isActive = value === option.value;
              const optionLabel = option.label;

              return (
                <div
                  key={option.value}
                  onClick={e => {
                    e.stopPropagation();
                    if (onChange) {
                      // Toggle: if already selected, clear it
                      const newValue = isActive ? undefined : option.value;
                      onChange(newValue);
                    }
                    // Close after a brief delay to show selection
                    setTimeout(() => setOpen(false), 150);
                  }}
                  className={`
                    flex items-center justify-between px-3 py-2 rounded-md
                    transition-all duration-150 cursor-pointer select-none
                    ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-sm font-semibold'
                        : 'hover:bg-gray-100 text-gray-700'
                    }
                  `}>
                  <div className="flex items-center gap-2">
                    {option.icon && (
                      <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                        {option.icon}
                      </div>
                    )}
                    <span className="text-sm font-medium">{optionLabel}</span>
                  </div>
                  {showCounts && option.count !== undefined && (
                    <span className="text-xs font-semibold opacity-80">{option.count}</span>
                  )}
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Inline mode: render from options as separate badges
  if (optionsWithCounts && optionsWithCounts.length > 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        {optionsWithCounts.map(option => {
          const isActive = value === option.value;
          const displayLabel = option.label;

          return (
            <StatusBadge.Item
              key={option.value}
              label={displayLabel}
              icon={option.icon}
              count={showCounts ? option.count : undefined}
              isActive={isActive}
              className={option.className}
              showCounter={showCounts}
              onClick={() => {
                if (onChange) {
                  // Toggle: if already selected, clear it
                  onChange(isActive ? undefined : option.value);
                }
              }}
            />
          );
        })}
      </div>
    );
  }

  // Manual mode: use children (backward compatible)
  return <div className="flex flex-wrap items-center gap-3">{children}</div>;
}

/**
 * Individual Badge item for displaying counts with styling - Clean neutral design
 * All badges are gray when inactive, only selected badge shows status color
 */
StatusBadge.Item = function BadgeItem({
  count,
  label,
  variant = 'secondary',
  className = '',
  isActive = false,
  onClick,
  showCounter = false,
  icon,
}: BadgeItemProps) {
  const { statusBadgeConfig } = useTableContext();

  // Get display text and styling
  // If label looks like a status key (contains underscore and no spaces), try statusBadgeConfig
  // Otherwise, assume it's already translated and use it directly
  const isStatusKey = label && label.includes('_') && !label.includes(' ');
  const displayText =
    statusBadgeConfig && isStatusKey
      ? statusBadgeConfig.getStatusDisplayText(label) || label
      : label;
  // Use icon from props first, then fall back to statusBadgeConfig
  const displayIcon =
    icon ||
    (statusBadgeConfig?.showIcon && statusBadgeConfig?.getStatusIcon && label
      ? statusBadgeConfig.getStatusIcon(label)
      : null);

  // Only use custom colors when active/selected - keeps it clean!
  const customBadgeClass =
    isActive && statusBadgeConfig && label ? statusBadgeConfig.getStatusClass(label) : '';

  // Determine badge class: prioritize className (from option) when active, then customBadgeClass, then default
  const badgeClass = isActive
    ? className || customBadgeClass || 'bg-primary text-primary-foreground'
    : '';

  return (
    <div
      onClick={onClick}
      className={`
        group inline-flex items-center gap-2 px-3 py-1.5 rounded-lg 
        transition-all duration-200 cursor-pointer
        ${
          isActive
            ? `ring-2 ring-primary ring-offset-2 scale-105 shadow-md font-semibold ${badgeClass}`
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
        }
      `}>
      {/* Icon (if available) */}
      {displayIcon && (
        <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">{displayIcon}</div>
      )}

      {/* Label */}
      <span className="text-sm font-medium">{displayText}</span>

      {/* Counter (if available and showCounter is true) - in a subtle badge */}
      {showCounter && count !== undefined && count !== null && (
        <span className="inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-md bg-black/10 font-semibold text-sm">
          {count}
        </span>
      )}
    </div>
  );
};

/**
 * Rows component for displaying and configuring table content
 * Now shows all data (no pagination slicing)
 */
function Rows({
  columns,
  rowRender,
  children,
  enableRowSelection,
  tableClassName,
  colGroup,
  trailingSpacer,
}: RowsProps) {
  const {
    data,
    isLoading,
    sortColumn,
    sortDirection,
    onSort,
    enableRowSelection: contextEnableRowSelection,
    selectedRowIds,
    onSelectionChange,
    getRowId,
  } = useTableContext();
  const { t } = useTranslation(['c-main']);

  const rowSelectionEnabled = enableRowSelection ?? contextEnableRowSelection ?? false;
  const totalColSpan =
    columns.length + (rowSelectionEnabled ? 1 : 0) + (trailingSpacer ? 1 : 0);

  // Check if all visible rows are selected
  const allSelected = useMemo(() => {
    if (!rowSelectionEnabled || data.length === 0) return false;
    return data.every((row, index) => selectedRowIds?.has(getRowId?.(row, index) || ''));
  }, [rowSelectionEnabled, data, selectedRowIds, getRowId]);

  // Check if some rows are selected
  const someSelected = useMemo(() => {
    if (!rowSelectionEnabled || data.length === 0) return false;
    const selectedCount = data.filter((row, index) =>
      selectedRowIds?.has(getRowId?.(row, index) || ''),
    ).length;
    return selectedCount > 0 && selectedCount < data.length;
  }, [rowSelectionEnabled, data, selectedRowIds, getRowId]);

  // Handle select all checkbox
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (!onSelectionChange) return;

      const newSelection: Set<string> = selectedRowIds
        ? new Set<string>(selectedRowIds)
        : new Set<string>();
      if (checked) {
        // Select all visible rows
        data.forEach((row, index) => {
          newSelection.add(getRowId?.(row, index) || '');
        });
      } else {
        // Deselect all visible rows
        data.forEach((row, index) => {
          newSelection.delete(getRowId?.(row, index) || '');
        });
      }
      onSelectionChange(newSelection);
    },
    [onSelectionChange, selectedRowIds, data, getRowId],
  );

  // Handle individual row checkbox
  const handleRowSelect = useCallback(
    (row: any, index: number, checked: boolean) => {
      if (!onSelectionChange) return;

      const rowId = getRowId?.(row, index) || '';
      const newSelection: Set<string> = selectedRowIds
        ? new Set<string>(selectedRowIds)
        : new Set<string>();

      if (checked) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }

      onSelectionChange(newSelection);
    },
    [onSelectionChange, selectedRowIds, getRowId],
  );

  return (
    <div className="overflow-x-auto">
      <Table className={tableClassName}>
        {colGroup}
        <TableHeader>
          <TableRow className="bg-gray-100">
            {rowSelectionEnabled && (
              <TableHead className="w-10">
                <div className="flex items-center">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? ('indeterminate' as any) : false}
                    onCheckedChange={handleSelectAll}
                    aria-label={t('selection.select_all')}
                  />
                </div>
              </TableHead>
            )}
            {columns.map(column => (
              <TableHead
                key={column.key}
                className={`text-start ${column.className || ''}`}>
                {column.sortable ? (
                  <Button
                    variant="ghost"
                    onClick={() => onSort(column.key)}
                    className={`ps-0 text-black subtitle2 ${
                      sortColumn === column.key ? 'text-primary' : ''
                    }`}>
                    {column.label}
                    <ArrowUpDown
                      className={`w-4 h-4 ms-2 ${
                        sortColumn === column.key
                          ? sortDirection === 'asc'
                            ? 'transform rotate-0'
                            : 'transform rotate-180'
                          : ''
                      }`}
                    />
                  </Button>
                ) : (
                  <span className="text-black subtitle2">{column.label}</span>
                )}
              </TableHead>
            ))}
            {trailingSpacer ? <TableHead className="w-full p-0" aria-hidden /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && data.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={totalColSpan} className="h-24">
                <Spinner className="mx-auto" />
              </TableCell>
            </TableRow>
          ) : !data.length ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={totalColSpan} className="h-24 text-center text-sm text-gray-500">
                {t('c-main:status.no_results_found')}
              </TableCell>
            </TableRow>
          ) : (
            <>
              {data.map((item, index) => {
                const rowId = getRowId?.(item, index) || '';
                const isSelected = rowSelectionEnabled && selectedRowIds?.has(rowId);

                return (
                  <TableRow
                    key={rowId}
                    data-state={isSelected ? 'selected' : undefined}
                    className={`${isSelected ? 'bg-muted' : ''} hover:bg-gray-50`}>
                    {rowSelectionEnabled && (
                      <TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={checked => handleRowSelect(item, index, !!checked)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          aria-label={t('select_row', 'Select row')}
                        />
                      </TableCell>
                    )}
                    {rowRender(item, index)}
                    {trailingSpacer ? (
                      <TableCell className="w-full p-0" aria-hidden />
                    ) : null}
                  </TableRow>
                );
              })}
              {children}
            </>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Footer container (now optional, sentinel is always shown)
 */
function Footer({ children }: { children?: React.ReactNode }) {
  if (children) {
    return (
      <div className="flex flex-col items-center justify-between gap-4 py-4 sm:flex-row">
        {children}
      </div>
    );
  }
  return null;
}

// Assign the sub-components to SharedTable
SharedTable.Search = SearchComponent;
SharedTable.Badge = StatusBadge;
SharedTable.Rows = Rows;
SharedTable.Footer = Footer;
SharedTable.Head = Head;
SharedTable.Mid = Mid;
